import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

/**
 * Fetch ALL services across all suppliers.
 * Fetches suppliers first, then services for each supplier in parallel.
 * Each service is enriched with `supplierUid` and `supplierName`.
 */
export const useAllServices = () => {
    return useQuery({
        queryKey: ['all-services'],
        queryFn: async () => {
            const [services, sups] = await Promise.all([
                api.getServices(),
                api.suppliers(),
            ]);
            const supplierMap = Object.fromEntries(sups.map((s) => [s.uid, s.name]));
            return services.map((svc) => ({
                ...svc,
                supplierUid: svc.supplier_uid,
                supplierName: supplierMap[svc.supplier_uid] ?? null,
            }));
        },
    });
};

export const useServicesBySupplier = (supplierUid) => {
    return useQuery({
        queryKey: ['services', { supplierUid }],
        queryFn: () => api.getServicesBySupplier(supplierUid),
        enabled: !!supplierUid,
    });
};

export const useCreateService = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ supplierUid, data }) => api.createService(supplierUid, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['services'] });
            queryClient.invalidateQueries({ queryKey: ['all-services'] });
        },
    });
};

export const useUpdateService = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ serviceUid, data }) => api.updateService(serviceUid, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['services'] });
            queryClient.invalidateQueries({ queryKey: ['all-services'] });
        },
    });
};

export const useDeleteService = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ serviceUid }) => api.deleteService(serviceUid),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['services'] });
            queryClient.invalidateQueries({ queryKey: ['all-services'] });
        },
    });
};
